import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Radio,
  Button,
  Tooltip,
  Space,
  Row,
  Col,
  Card,
} from "antd";
import {
  UserOutlined,
  LockOutlined,
  MailOutlined,
  PhoneOutlined,
  InfoCircleOutlined,
  ArrowLeftOutlined,
} from "@ant-design/icons";
import PhoneInput from "antd-phone-input";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  signupApi,
  signinApi,
  checkUserApi,
  sendOtpApi,
  validateOtpApi,
  profileApi,
  forgotPasswordSendOtpApi,
  forgotPasswordResetApi,
  clearError,
} from "../../store/features/user";
import { setAuthCookies, removeAuthCookies } from "../../cookies";
import { getAccessToken, getRefreshToken } from "../../cookies";
import { useToast } from "../../components";
import "./signup.css";

const RESEND_COOLDOWN = 60; // 1 minute in seconds

const Signup = () => {
  const [form] = Form.useForm();
  const [step, setStep] = useState(1); // 1: contact, 2a: signin password, 2b: OTP, 3: set password
  const [usePhone] = useState(false); // Single-field toggle: email vs phone
  const [contact, setContact] = useState({ type: "email", value: "" });
  const [otp, setOtp] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [isSendingForgotOtp, setIsSendingForgotOtp] = useState(false);
  const [isResettingPassword, setIsResettingPassword] = useState(false);

  // Watch form fields for forgot password reset step
  const watchedPassword = Form.useWatch("password", form);
  const watchedConfirmPassword = Form.useWatch("confirmPassword", form);
  const timerRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoading, error } = useSelector((state) => state.user);
  const toast = useToast();

  const passwordRules =
    "Password must contain: at least 1 uppercase letter, at least 1 lowercase letter, at least 1 symbol (!@#$%^&*()-_=+{}|:;'\",./<>?) and should be at least 8 characters in total";

  useEffect(() => {
    // Check if user is already authenticated by calling the profile API
    const checkAuthentication = async () => {
      const accessToken = getAccessToken();
      const refreshToken = getRefreshToken();

      if (accessToken && refreshToken) {
        try {
          // Use dispatch with profileApi for proper Redux state management
          const result = await dispatch(profileApi());

          if (profileApi.fulfilled.match(result)) {
            // Profile API call succeeded, token is valid, redirect to explore page
            navigate("/explore");
          }
          // If profileApi call fails (rejected), don't redirect, let user sign in normally
        } catch (error) {
          console.error("Profile API dispatch failed:", error);
          // If dispatch fails, don't redirect
        }
      }
    };

    checkAuthentication();
  }, [dispatch, navigate]);

  // Error handling for all steps
  useEffect(() => {
    if (error) {
      toast.error("Error", error);
      dispatch(clearError());
    }
  }, [error, toast, dispatch]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    if (timerRef.current) clearInterval(timerRef.current);

    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendTimer]);

  const startResendTimer = () => {
    setResendTimer(RESEND_COOLDOWN);
  };

  const validatePassword = (_, value) => {
    if (!value) {
      return Promise.reject(new Error("Password is required"));
    }

    const hasUppercase = /[A-Z]/.test(value);
    const hasLowercase = /[a-z]/.test(value);
    const hasSymbol = /[!@#$%^&*()\-_=+{}|:;'",.<>/?]/.test(value);

    if (!hasUppercase || !hasLowercase || !hasSymbol || !(value.length >= 8)) {
      return Promise.reject(new Error(passwordRules));
    }

    return Promise.resolve();
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // Step 1: Check if user exists
  const onContinue = async (values) => {
    try {
      const email = values.email;
      const phone = values.phone;
      let identifierData = {};

      const type = email ? "email" : "phone";
      let value;
      if (email) {
        identifierData = { email };
        value = email;
      } else if (phone && typeof phone === "object") {
        // antd-phone-input returns an object with phone parts
        // Construct the full phone number string with country code
        const { countryCode, areaCode, phoneNumber } = phone;
        const fullPhone = `+${countryCode}${areaCode}${phoneNumber}`;
        // Send the full phone number string to API (with country code)
        identifierData = { phone: fullPhone };
        // Normalize: strip leading '+' for storage/display consistency
        value = fullPhone.startsWith("+") ? fullPhone.substring(1) : fullPhone;
      } else if (phone && typeof phone === "string") {
        // Ensure string phone has '+' prefix for API consistency
        const fullPhone = phone.startsWith("+") ? phone : `+${phone}`;
        identifierData = { phone: fullPhone };
        value = phone.startsWith("+") ? phone.substring(1) : phone;
      }
      setContact({ type, value });

      const result = await dispatch(checkUserApi(identifierData));

      if (checkUserApi.fulfilled.match(result)) {
        const { exists } = result.payload.data;

        if (exists) {
          // Existing user → go to signin password screen
          setStep("2a");
        } else {
          // New user → send OTP and go to OTP screen
          setIsSendingOtp(true);
          try {
            const otpResult = await dispatch(sendOtpApi(identifierData));
            if (sendOtpApi.fulfilled.match(otpResult)) {
              setStep("2b");
              startResendTimer();
            } else {
              toast.error(
                "Failed to Send OTP",
                otpResult.payload || "Please try again.",
              );
            }
          } finally {
            setIsSendingOtp(false);
          }
        }
      }
    } catch (err) {
      console.error("Continue error:", err);
    }
  };

  // Step 2a: Sign in existing user
  const onSigninPassword = async (values) => {
    try {
      const signinData = {
        password: values.password,
      };
      if (contact.type === "email") {
        signinData.email = contact.value;
      } else {
        signinData.phone = contact.value;
      }

      const result = await dispatch(signinApi(signinData));

      if (signinApi.fulfilled.match(result)) {
        const { tokens, user } = result.payload.data;

        // Check if user role matches the expected role for this platform
        const expectedRole = process.env.REACT_APP_ROLE;
        if (user.role !== expectedRole) {
          removeAuthCookies();

          toast.error(
            "Unauthorized",
            "Please sign in with a customer account to access this platform. Redirecting to the coach platform.",
          );
          setTimeout(() => {
            window.location.href = new URL(
              `${process.env.REACT_APP_COACH_URL}/signin`,
            );
          }, 5000);
          return;
        }

        // Set auth cookies from the API response
        setAuthCookies(tokens.accessToken, tokens.refreshToken);

        form.resetFields();
        navigate("/explore");
      }
    } catch (err) {
      console.error("Signin error:", err);
    }
  };

  // Step 2b: Verify OTP
  const onVerifyOtp = async () => {
    try {
      if (!otp || otp.length !== 6) {
        toast.error(
          "Invalid OTP",
          "Please enter the 6-digit code sent to you.",
        );
        return;
      }

      const otpData = { otp };
      if (contact.type === "email") {
        otpData.email = contact.value;
      } else {
        otpData.phone = contact.value;
      }

      const result = await dispatch(validateOtpApi(otpData));

      if (validateOtpApi.fulfilled.match(result)) {
        // OTP verified, go to set password screen
        setStep(3);
        setOtp("");
      }
    } catch (err) {
      console.error("Verify OTP error:", err);
    }
  };

  // Step 2b: Resend OTP
  const onResendOtp = async () => {
    if (resendTimer > 0 || isSendingOtp) return;

    try {
      setIsSendingOtp(true);
      const identifierData =
        contact.type === "email"
          ? { email: contact.value }
          : { phone: contact.value };

      const result = await dispatch(sendOtpApi(identifierData));

      if (sendOtpApi.fulfilled.match(result)) {
        toast.success("OTP Resent", "A new verification code has been sent.");
        startResendTimer();
      } else {
        toast.error(
          "Failed to Resend OTP",
          result.payload || "Please try again.",
        );
      }
    } finally {
      setIsSendingOtp(false);
    }
  };

  // Step 3: Create account
  const onFinish = async (values) => {
    try {
      const signupData = {
        firstName: values.firstName,
        lastName: values.lastName || null,
        gender: values.gender,
        password: values.password,
      };

      if (contact.type === "email") {
        signupData.email = contact.value;
      } else {
        signupData.phone = contact.value;
      }

      const cleanedSignupData = Object.entries(signupData).reduce(
        (acc, [key, value]) => {
          if (value) {
            acc[key] = value;
          }
          return acc;
        },
        {},
      );

      const result = await dispatch(signupApi(cleanedSignupData));

      if (signupApi.fulfilled.match(result)) {
        toast.success("Signup Successful", "Welcome to Talk2Coach!");
        form.resetFields();
        navigate("/explore");
      }
    } catch (err) {
      console.error("Signup error:", err);
    }
  };

  // Forgot Password: Send OTP
  const onForgotPasswordSendOtp = async () => {
    try {
      if (!forgotEmail) {
        toast.error("Email Required", "Please enter your email address.");
        return;
      }

      setIsSendingForgotOtp(true);
      const result = await dispatch(
        forgotPasswordSendOtpApi({ email: forgotEmail }),
      );

      if (forgotPasswordSendOtpApi.fulfilled.match(result)) {
        toast.success(
          "OTP Sent",
          "A password reset code has been sent to your email.",
        );
        setStep("forgot-reset");
        form.resetFields();
      } else {
        toast.error(
          "Failed to Send OTP",
          result.payload || "Please try again.",
        );
      }
    } catch (err) {
      console.error("Forgot password send OTP error:", err);
    } finally {
      setIsSendingForgotOtp(false);
    }
  };

  // Forgot Password: Reset Password
  const onForgotPasswordReset = async (values) => {
    try {
      setIsResettingPassword(true);
      const result = await dispatch(
        forgotPasswordResetApi({
          otp: forgotOtp,
          password: values.password,
        }),
      );

      if (forgotPasswordResetApi.fulfilled.match(result)) {
        toast.success(
          "Password Reset",
          "Your password has been reset successfully. Please sign in.",
        );
        setForgotEmail("");
        setForgotOtp("");
        setStep(1);
        form.resetFields();
      } else {
        toast.error(
          "Failed to Reset Password",
          result.payload || "Please try again.",
        );
      }
    } catch (err) {
      console.error("Forgot password reset error:", err);
    } finally {
      setIsResettingPassword(false);
    }
  };

  const goBack = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setResendTimer(0);
    setStep(1);
    setOtp("");
    form.resetFields();
  };

  const goBackFromForgotEmail = () => {
    setForgotEmail("");
    setForgotOtp("");
    setStep("2a");
    form.resetFields();
  };

  const goBackToForgotEmail = () => {
    setForgotOtp("");
    setStep("forgot-email");
    form.resetFields();
  };

  const renderStepIndicator = () => {
    return (
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
          }}
        >
          {[1, 2, 3].map((s) => {
            const isActive =
              step === "2a" || step === "2b" ? s === 2 : step >= s;
            const isComplete = step === 3 && s < 3;
            return (
              <React.Fragment key={s}>
                {s > 1 && (
                  <div
                    style={{
                      width: "30px",
                      height: "2px",
                      backgroundColor: isComplete ? "#10B981" : "#d9d9d9",
                    }}
                  />
                )}
                <div
                  style={{
                    width: "28px",
                    height: "28px",
                    borderRadius: "50%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    fontSize: "13px",
                    fontWeight: "bold",
                    color: isActive || isComplete ? "#fff" : "#999",
                    backgroundColor:
                      isActive || isComplete ? "#10B981" : "#fff",
                    border:
                      isActive || isComplete ? "none" : "1px solid #d9d9d9",
                  }}
                >
                  {isComplete ? "✓" : s}
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontSize: "11px",
            color: "#999",
            marginTop: "6px",
            maxWidth: "220px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <span>Contact</span>
          <span>Verify</span>
          <span>Password</span>
        </div>
      </div>
    );
  };

  // Step 1: Contact entry — single field with Email/Phone toggle
  const renderContactStep = () => {
    return (
      <>
        <div className="signup-header">
          <img
            src={process.env.PUBLIC_URL + "/branding.png"}
            alt="Talk2Coach"
            className="signup-branding"
          />
          <p className="signup-subtitle">Login or Signup</p>
          <p className="signup-helper">Enter your email to get started</p>
        </div>

        {!usePhone ? (
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Please enter a valid email" },
            ]}
          >
            <Input
              prefix={<MailOutlined />}
              placeholder="Enter email address"
            />
          </Form.Item>
        ) : (
          <Form.Item
            name="phone"
            label="Phone"
            rules={[
              { required: true, message: "Phone number is required" },
              {
                validator: (_, value) => {
                  if (!value) {
                    return Promise.reject(
                      new Error("Phone number is required"),
                    );
                  }
                  // antd-phone-input returns an object with phone parts
                  // Extract the full phone number with country code
                  let phoneString = "";
                  if (typeof value === "object" && value !== null) {
                    const { countryCode, areaCode, phoneNumber } = value;
                    phoneString = `+${countryCode}${areaCode}${phoneNumber}`;
                  } else if (typeof value === "string") {
                    phoneString = value;
                  } else {
                    return Promise.reject(
                      new Error("Please enter a valid phone number"),
                    );
                  }
                  // Remove '+' to get just the digits for validation
                  const phoneDigits = phoneString.replace(/^\+/, "");
                  if (!/^\d{7,15}$/.test(phoneDigits)) {
                    return Promise.reject(
                      new Error("Please enter a valid phone number"),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <PhoneInput placeholder="Enter phone number" defaultCountry="IN" />
          </Form.Item>
        )}

        {/* Temporarily disabled - SMS provider not configured
        <div
          style={{
            fontSize: "14px",
            color: "#666",
            marginBottom: "16px",
            textAlign: "center",
          }}
        >
          {!usePhone ? (
            <Button
              type="link"
              onClick={toggleLoginMethod}
              style={{ color: "#10B981", padding: 0 }}
            >
              Continue with Phone
            </Button>
          ) : (
            <Button
              type="link"
              onClick={toggleLoginMethod}
              style={{ color: "#10B981", padding: 0 }}
            >
              Continue with Email
            </Button>
          )}
        </div>
        */}

        <Form.Item style={{ marginBottom: "12px" }}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={isLoading}
              block
              size="large"
              style={{
                backgroundColor: "#10B981",
                borderColor: "#10B981",
                height: "48px",
                fontSize: "16px",
                fontWeight: "bold",
              }}
            >
              Continue
            </Button>
          </Space>
        </Form.Item>
      </>
    );
  };

  const renderSigninPasswordStep = () => {
    return (
      <>
        <div
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "bold",
            color: "#10B981",
            marginBottom: "8px",
          }}
        >
          Welcome Back
        </div>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            marginBottom: "24px",
            fontSize: "14px",
          }}
        >
          An account already exists with this{" "}
          {contact.type === "email" ? "email address" : "phone number"}
        </p>

        <Form.Item label={contact.type === "email" ? "Email" : "Phone"}>
          <Input
            prefix={
              contact.type === "email" ? <MailOutlined /> : <PhoneOutlined />
            }
            value={
              contact.type === "email" ? contact.value : `+${contact.value}`
            }
            disabled
            style={{ color: "#333" }}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label="Password"
          rules={[{ required: true, message: "Password is required" }]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter password"
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          block
          size="large"
          style={{
            backgroundColor: "#10B981",
            borderColor: "#10B981",
            height: "48px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {isLoading ? "Signing In..." : "Sign In"}
        </Button>

        <Button
          type="link"
          onClick={() => {
            setForgotEmail(contact.type === "email" ? contact.value : "");
            setStep("forgot-email");
            form.resetFields();
          }}
          block
          style={{ marginTop: "8px", color: "#10B981" }}
        >
          Forgot Password?
        </Button>

        <Button
          type="link"
          onClick={goBack}
          block
          style={{ marginTop: "4px", color: "#666" }}
        >
          <ArrowLeftOutlined /> Back
        </Button>
      </>
    );
  };

  const renderOtpStep = () => {
    const isEmail = contact.type === "email";
    return (
      <>
        <div
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "bold",
            color: "#10B981",
            marginBottom: "8px",
          }}
        >
          {isEmail ? "Verify Email Address" : "Verify Phone Number"}
        </div>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            marginBottom: "24px",
            fontSize: "14px",
          }}
        >
          We've sent a 6-digit verification code to{" "}
          <strong>{contact.value}</strong>
        </p>

        <Form.Item label={isEmail ? "Email" : "Phone"}>
          <Input
            prefix={isEmail ? <MailOutlined /> : <PhoneOutlined />}
            value={isEmail ? contact.value : `+${contact.value}`}
            disabled
            style={{ color: "#333" }}
          />
        </Form.Item>

        <Form.Item label="Verification Code">
          <Input
            prefix={<LockOutlined />}
            placeholder="Enter 6-digit code"
            value={otp}
            onChange={(e) =>
              setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            maxLength={6}
            style={{
              textAlign: "center",
              fontSize: "24px",
              letterSpacing: "8px",
              fontWeight: "bold",
            }}
          />
        </Form.Item>

        <Button
          type="primary"
          onClick={onVerifyOtp}
          loading={isLoading}
          block
          size="large"
          style={{
            backgroundColor: "#10B981",
            borderColor: "#10B981",
            height: "48px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {isLoading ? "Verifying..." : "Verify & Continue"}
        </Button>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "16px",
          }}
        >
          <Button
            type="link"
            onClick={goBack}
            style={{ color: "#666", padding: 0 }}
          >
            <ArrowLeftOutlined /> Back
          </Button>

          {resendTimer > 0 ? (
            <span style={{ fontSize: "13px", color: "#999" }}>
              Resend OTP in {formatTimer(resendTimer)}
            </span>
          ) : (
            <Button
              type="link"
              onClick={onResendOtp}
              loading={isSendingOtp}
              style={{ color: "#10B981", padding: 0 }}
            >
              Resend OTP
            </Button>
          )}
        </div>
      </>
    );
  };

  const renderPasswordStep = () => {
    const isEmail = contact.type === "email";
    return (
      <>
        <div
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "bold",
            color: "#10B981",
            marginBottom: "8px",
          }}
        >
          Create Your Account
        </div>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            marginBottom: "24px",
            fontSize: "14px",
          }}
        >
          Set a password and tell us a bit about yourself
        </p>

        <Form.Item label={isEmail ? "Email" : "Phone"}>
          <Input
            prefix={isEmail ? <MailOutlined /> : <PhoneOutlined />}
            value={isEmail ? contact.value : `+${contact.value}`}
            disabled
            style={{ color: "#333" }}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="firstName"
              label="First Name"
              rules={[
                { required: true, message: "First name is required" },
                {
                  min: 2,
                  max: 50,
                  message: "First name must be 2-50 characters",
                },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter first name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="lastName"
              label="Last Name (Optional)"
              rules={[
                {
                  min: 2,
                  max: 50,
                  message: "Last name must be 2-50 characters",
                },
              ]}
            >
              <Input prefix={<UserOutlined />} placeholder="Enter last name" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="gender"
          label="Gender"
          rules={[{ required: true, message: "Gender is required" }]}
        >
          <Radio.Group>
            <Radio value="MALE">Male</Radio>
            <Radio value="FEMALE">Female</Radio>
            <Radio value="OTHER">Other</Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="password"
          label={
            <Space>
              Password
              <Tooltip title={passwordRules} placement="right">
                <InfoCircleOutlined style={{ color: "#10B981" }} />
              </Tooltip>
            </Space>
          }
          rules={[
            { required: true, message: "Password is required" },
            { validator: validatePassword },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter password"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={["password"]}
          hasFeedback
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") !== value) {
                  return Promise.reject(new Error("Passwords do not match"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm password"
          />
        </Form.Item>

        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          block
          size="large"
          style={{
            backgroundColor: "#10B981",
            borderColor: "#10B981",
            height: "48px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {isLoading ? "Creating Account..." : "Create Account"}
        </Button>

        <Button
          type="link"
          onClick={goBack}
          block
          style={{ marginTop: "12px", color: "#666" }}
        >
          <ArrowLeftOutlined /> Back
        </Button>
      </>
    );
  };

  // Forgot Password: Verify Email screen
  const renderForgotEmailStep = () => {
    return (
      <>
        <div
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "bold",
            color: "#10B981",
            marginBottom: "8px",
          }}
        >
          Verify Email Address
        </div>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            marginBottom: "24px",
            fontSize: "14px",
          }}
        >
          Enter your email address and we'll send you a password reset code
        </p>

        <Form.Item
          name="forgotEmail"
          label="Email"
          rules={[
            { required: true, message: "Email is required" },
            { type: "email", message: "Please enter a valid email" },
          ]}
        >
          <Input
            prefix={<MailOutlined />}
            placeholder="Enter email address"
            value={forgotEmail}
            onChange={(e) => setForgotEmail(e.target.value)}
          />
        </Form.Item>

        <Button
          type="primary"
          onClick={onForgotPasswordSendOtp}
          loading={isSendingForgotOtp}
          block
          size="large"
          style={{
            backgroundColor: "#10B981",
            borderColor: "#10B981",
            height: "48px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {isSendingForgotOtp ? "Sending OTP..." : "Send OTP"}
        </Button>

        <Button
          type="link"
          onClick={goBackFromForgotEmail}
          block
          style={{ marginTop: "12px", color: "#666" }}
        >
          <ArrowLeftOutlined /> Back
        </Button>
      </>
    );
  };

  // Forgot Password: Reset Password screen
  const renderForgotResetStep = () => {
    const isOtpValid = forgotOtp.length === 6;
    const passwordValue = watchedPassword || "";
    const confirmValue = watchedConfirmPassword || "";
    const isPasswordValid =
      passwordValue &&
      /[A-Z]/.test(passwordValue) &&
      /[a-z]/.test(passwordValue) &&
      /[!@#$%^&*()\-_=+{}|:;'",.<>/?]/.test(passwordValue) &&
      passwordValue.length >= 8;
    const isConfirmValid = confirmValue && confirmValue === passwordValue;
    const isResetEnabled = isOtpValid && isPasswordValid && isConfirmValid;

    return (
      <>
        <div
          style={{
            textAlign: "center",
            fontSize: "28px",
            fontWeight: "bold",
            color: "#10B981",
            marginBottom: "8px",
          }}
        >
          Reset Password
        </div>
        <p
          style={{
            textAlign: "center",
            color: "#666",
            marginBottom: "24px",
            fontSize: "14px",
          }}
        >
          Enter the 6-digit code sent to <strong>{forgotEmail}</strong> and set
          a new password
        </p>

        <Form.Item label="Verification Code">
          <Input
            prefix={<LockOutlined />}
            placeholder="Enter 6-digit code"
            value={forgotOtp}
            onChange={(e) =>
              setForgotOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
            }
            maxLength={6}
            style={{
              textAlign: "center",
              fontSize: "24px",
              letterSpacing: "8px",
              fontWeight: "bold",
            }}
          />
        </Form.Item>

        <Form.Item
          name="password"
          label={
            <Space>
              New Password
              <Tooltip title={passwordRules} placement="right">
                <InfoCircleOutlined style={{ color: "#10B981" }} />
              </Tooltip>
            </Space>
          }
          rules={[
            { required: true, message: "Password is required" },
            { validator: validatePassword },
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Enter new password"
          />
        </Form.Item>

        <Form.Item
          name="confirmPassword"
          label="Confirm Password"
          dependencies={["password"]}
          hasFeedback
          rules={[
            { required: true, message: "Please confirm your password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") !== value) {
                  return Promise.reject(new Error("Passwords do not match"));
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password
            prefix={<LockOutlined />}
            placeholder="Confirm new password"
          />
        </Form.Item>

        <Button
          type="primary"
          onClick={() => form.submit()}
          loading={isResettingPassword}
          disabled={!isResetEnabled}
          block
          size="large"
          style={{
            backgroundColor: "#10B981",
            borderColor: "#10B981",
            height: "48px",
            fontSize: "16px",
            fontWeight: "bold",
          }}
        >
          {isResettingPassword ? "Resetting..." : "Reset Password"}
        </Button>

        <Button
          type="link"
          onClick={goBackToForgotEmail}
          block
          style={{ marginTop: "12px", color: "#666" }}
        >
          <ArrowLeftOutlined /> Back
        </Button>
      </>
    );
  };

  return (
    <div className="signup-page">
      <Card className="signup-card">
        {step !== 1 &&
          step !== "forgot-email" &&
          step !== "forgot-reset" &&
          renderStepIndicator()}

        <Form
          form={form}
          name="signup"
          onFinish={
            step === 1
              ? onContinue
              : step === "2a"
                ? onSigninPassword
                : step === "forgot-reset"
                  ? onForgotPasswordReset
                  : onFinish
          }
          layout="vertical"
          size="large"
          className="signup-form"
        >
          <div className="auth-step-wrapper" key={step}>
            {step === 1 && renderContactStep()}
            {step === "2a" && renderSigninPasswordStep()}
            {step === "2b" && renderOtpStep()}
            {step === 3 && renderPasswordStep()}
            {step === "forgot-email" && renderForgotEmailStep()}
            {step === "forgot-reset" && renderForgotResetStep()}
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default Signup;
